<section class="manage-keychains">
  <header class="page-header">
    <span class="page-kicker">Administración</span>
    <h1>Gestión de llaveros</h1>
    <p>Configura los datos del producto y su tabla de precios.</p>
  </header>

  <article class="admin-card">
    <div class="card-header">
      <div>
        <h2>Datos del producto</h2>
        <p>Información general, categorías e imágenes.</p>
      </div>
    </div>

    @if (product(); as currentProduct) {
      <div class="product-form">
        <label class="form-field form-field--full">
          <span>Nombre</span>
          <input type="text" [(ngModel)]="productName" />
        </label>

        <label class="form-field form-field--full">
          <span>Descripción</span>
          <textarea
            rows="4"
            [(ngModel)]="productDescription"
          ></textarea>
        </label>

        <label class="form-field">
          <span>Categoría</span>

          <select
            [(ngModel)]="mainCategoryId"
            (ngModelChange)="onMainCategoryChange()"
          >
            <option value="">— Sin categoría —</option>

            @for (category of mainCategories(); track category.id) {
              <option [value]="category.id">
                {{ category.name }}
              </option>
            }
          </select>
        </label>

        <label class="form-field">
          <span>Subcategoría</span>

          <select
            [(ngModel)]="subCategoryId"
            [disabled]="!subCategories().length"
          >
            <option value="">— Sin subcategoría —</option>

            @for (subCategory of subCategories(); track subCategory.id) {
              <option [value]="subCategory.id">
                {{ subCategory.name }}
              </option>
            }
          </select>
        </label>

        <div class="form-section">
          <h3>Fotos</h3>
          @for (key of [uploaderKey()]; track key) {
            <app-image-uploader
              [maxImages]="4"
              [initialUrls]="productImages()"
              [optimizeForCatalog]="true"
              (imagesChange)="onImagesUpdated($event)"
            ></app-image-uploader>
          }
        </div>
      </div>

      <div class="card-actions">
        <button
          class="button button--success"
          type="button"
          [disabled]="isSavingProduct()"
          (click)="saveProduct()"
        >
          {{ isSavingProduct() ? 'Guardando...' : 'Guardar producto' }}
        </button>
      </div>
    } @else {
      <div class="loading-state">
        Cargando producto...
      </div>
    }
  </article>

  <article class="admin-card price-card">
    <div class="card-header">
      <div>
        <h2>Gestión de llaveros por cantidad de letras</h2>
      </div>
    </div>

    @if (isLoading()) {
      <div class="loading-state">
        Cargando configuración...
      </div>
    }

    <div class="price-toolbar">
      <div class="price-fields">
        <label class="compact-field">
          <span>Precio base</span>

          <input
            type="number"
            min="0"
            [(ngModel)]="editableBasePrice"
            (keydown.enter)="applyPricePreview()"
          />
        </label>

        <label class="compact-field">
          <span>Valor de salto</span>

          <input
            type="number"
            min="0"
            [(ngModel)]="editableStepPrice"
            (keydown.enter)="applyPricePreview()"
          />
        </label>

        <button
          class="icon-button table-refresh-button"
          type="button"
          aria-label="Actualizar tabla de precios"
          title="Actualizar tabla de precios"
          [disabled]="isLoading()"
          (click)="applyPricePreview()"
        >
          ↻
        </button>
      </div>

      <div class="price-formula">
        <span>Fórmula aplicada</span>

        <strong>
          Precio base + (letras adicionales + grosor) × salto
        </strong>
      </div>
    </div>

    <div class="matrix-heading">
      <div>
        <h3>Tabla de precios</h3>

        <p>
          Cantidad actual:
          <strong>{{ minLetters() }}–{{ maxLetters() }} letras</strong>
        </p>
      </div>

      <div class="letter-buttons">
        <button
          class="icon-button"
          type="button"
          aria-label="Reducir cantidad máxima de letras"
          title="Reducir cantidad máxima de letras"
          [disabled]="isLoading() || isAdjustingLetters()"
          (click)="requestLetterAdjustment('remove')"
        >
          −
        </button>

        <button
          class="icon-button"
          type="button"
          aria-label="Aumentar cantidad máxima de letras"
          title="Aumentar cantidad máxima de letras"
          [disabled]="isLoading() || isAdjustingLetters()"
          (click)="requestLetterAdjustment('add')"
        >
          +
        </button>
      </div>
    </div>

    <div class="table-container">
      <table class="price-matrix">
        <thead>
          <tr>
            <th># / Grosor</th>

            @for (thickness of thicknesses(); track thickness.id) {
              <th>{{ thickness.label }}</th>
            }
          </tr>
        </thead>

        <tbody>
          @for (row of priceRows(); track row.letterCount) {
            <tr>
              <th scope="row">
                {{ row.letterCount }}
                {{ row.letterCount === 1 ? 'letra' : 'letras' }}
              </th>

              @for (price of row.prices; track $index) {
                <td>
                  {{ price | number: '1.0-0' }}
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                class="empty-cell"
                [attr.colspan]="thicknesses().length + 1"
              >
                No hay datos de configuración.
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="card-actions">
      <button
        class="button button--success"
        type="button"
        [disabled]="isUpdatingPrice()"
        (click)="updatePrice()"
      >
        {{
          isUpdatingPrice()
            ? 'Guardando...'
            : 'Guardar cambios'
        }}
      </button>
    </div>
  </article>

  @if (isLetterModalOpen()) {
    <div
      class="modal-backdrop"
      role="presentation"
      (click)="closeLetterModal()"
    >
      <section
        class="confirmation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="letter-modal-title"
        aria-describedby="letter-modal-description"
        (click)="$event.stopPropagation()"
      >
        <div class="confirmation-modal__header">
          <h2 id="letter-modal-title">
            {{
              pendingLetterAction() === 'add'
                ? 'Agregar cantidad de letras'
                : 'Reducir cantidad de letras'
            }}
          </h2>

          <button
            class="modal-close-button"
            type="button"
            aria-label="Cerrar"
            [disabled]="isAdjustingLetters()"
            (click)="closeLetterModal()"
          >
            ×
          </button>
        </div>

        <p id="letter-modal-description">
          @if (pendingLetterAction() === 'add') {
            Se agregará una nueva cantidad de letras al límite máximo de la
            tabla de precios.
          } @else {
            Se eliminará la última cantidad de letras de la tabla de precios.
          }
        </p>

        <div class="confirmation-modal__actions">
          <button
            class="button button--neutral"
            type="button"
            [disabled]="isAdjustingLetters()"
            (click)="closeLetterModal()"
          >
            Cancelar
          </button>

          <button
            class="button"
            type="button"
            [class.button--success]="pendingLetterAction() === 'add'"
            [class.button--danger-soft]="
              pendingLetterAction() === 'remove'
            "
            [disabled]="isAdjustingLetters()"
            (click)="confirmLetterAdjustment()"
          >
            {{
              isAdjustingLetters()
                ? 'Actualizando...'
                : 'Confirmar'
            }}
          </button>
        </div>
      </section>
    </div>
  }
</section>
